import DataGrid, { Button, Column, Editing, Item, LoadPanel, Paging, Scrolling, Toolbar } from "devextreme-react/data-grid";
import DxButton from "devextreme-react/button";
import { Popup } from "devextreme-react/popup";
import type DataSource from "devextreme/data/data_source";
import React, { useEffect, useId, useRef, useState } from "react";
import { Tooltip } from "devextreme-react/tooltip";
import { FileLink, FileListHeader } from "../../FileLink";
import type { FileDto } from "../../../../../dtos/FileDto";
import type { GrantDto } from "../../../../../dtos/GrantDto";
import type { StepDto } from "../../../../../dtos/StepDto";
import { CommentType } from "../../../../../dtos/CommentType";
import type { SlotRow } from "../hooks/useTaskFilesController";
import TaskFileUploader from "./FileUploader";
import { uploadExtensions } from "../../stepFiles";
import "./SubTaskFilesView.css";

// Below this the slot list's action buttons show icons only, and the plain list drops its column
// headings: with only a name and a date on each row, they say nothing the rows don't.
export const COMPACT_SLOT_LIST_WIDTH = 560;

export type ISubTaskFilesViewProps = {
    dataSource: DataSource<FileDto, string>;
    onNameClick: (id: string, name: string) => void;
    allowDeleting: (fileData: any) => boolean;
    downloadButtonOptions: any;
    visible: boolean;
    showHeader: boolean;
    headerText: string;
    canDownloadAll: boolean;
    noDataText: string;
    // Date beside each name and the hover tooltip. Off for template attachments, which have none
    // of that information.
    showFileInfo?: boolean;

    // Submission slot mode: one row per slot (filled/empty) with an in-row upload/replace dialog.
    slotMode?: boolean;
    slots?: SlotRow[];
    // Files still in SharePoint that no configured document claims, and the folder holding them.
    orphanFiles?: FileDto[];
    orphanFolderUrl?: string | null;
    canModifySlots?: boolean;
    slotDialog?: { open: boolean; slot: SlotRow | null };
    onSlotAction?: (slot: SlotRow) => void;
    onSlotDelete?: (slot: SlotRow) => void;
    deleteFileForReplace?: (fileId: string) => Promise<void>;
    closeSlotDialog?: () => void;
    grant?: GrantDto;
    step?: StepDto;
    xsrfToken?: string;
};

export function SubTaskFilesView({
    dataSource,
    onNameClick,
    allowDeleting,
    downloadButtonOptions,
    visible,
    showHeader,
    headerText,
    canDownloadAll,
    noDataText,
    showFileInfo = true,
    slotMode = false,
    slots = [],
    orphanFiles = [],
    orphanFolderUrl,
    canModifySlots = false,
    slotDialog,
    onSlotAction,
    onSlotDelete,
    deleteFileForReplace,
    closeSlotDialog,
    grant,
    step,
    xsrfToken,
}: ISubTaskFilesViewProps) {

    const orphanTooltipId = `orphan-files-${useId().replace(/[:]/g, "")}`;

    // A list beside a long instructions block is narrow: the slot buttons drop to their icons and
    // the plain list loses its column headings. Measured here rather than in CSS because the column
    // width and the header row are grid settings, not styles.
    const listRef = useRef<HTMLDivElement>(null);
    const [compact, setCompact] = useState(false);
    useEffect(() => {
        const el = listRef.current;
        if (!el || typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(([entry]) => setCompact(entry.contentRect.width < COMPACT_SLOT_LIST_WIDTH));
        observer.observe(el);
        return () => observer.disconnect();
    }, [slotMode]);

    // Submission slot list: a grid with one row per configured slot, plus an in-row
    // upload/replace dialog that hosts the shared file upload component.
    if (slotMode) {
        // Same link as every other file list: name, date and the hover tooltip. The grid shares
        // its row with the instructions and has no space for columns of its own.
        const fileLink = (c: any) => {
            const f = c.data?.file as FileDto | null;
            if (!f) return <span style={{ color: "#999", fontStyle: "italic" }}>No file</span>;
            return <FileLink file={f} onClick={() => onNameClick(f.id, f.name)} showInfo={showFileInfo} testId="slot-file-name" />;
        };
        const actionsCell = (c: any) => {
            const s = c.data as SlotRow;
            if (!canModifySlots) return null;
            return (
                <div style={{ display: "flex", gap: "6px", justifyContent: "flex-end" }}>
                    <DxButton
                        text={compact ? undefined : s.file ? "Replace" : "Upload"}
                        hint={s.file ? "Replace file" : "Upload file"}
                        icon={s.file ? "refresh" : "upload"}
                        stylingMode="outlined"
                        onClick={() => onSlotAction?.(s)}
                    />
                    {/* Always present (disabled when empty) so the action buttons line up across rows */}
                    <DxButton icon="trash" stylingMode="text" type="danger" hint="Delete file" disabled={!s.file} onClick={() => onSlotDelete?.(s)} />
                </div>
            );
        };

        const renderDialogContent = () => {
            const slot = slotDialog?.slot;
            if (!slot || !grant || !step) return <div style={{ padding: 12 }}>Unable to open uploader.</div>;
            const existing = slot.file;
            return (
                <div style={{ padding: 12 }}>
                    {existing ? (
                        <div style={{ fontSize: 13, color: "#b54708", background: "#fffaeb", border: "1px solid #fedf89", borderRadius: 4, padding: "8px 10px", marginBottom: 10 }}>
                            Uploading here will replace the current file: <strong>{existing.name}</strong>
                        </div>
                    ) : null}
                    {slot.allowedExtensions?.length ? (
                        <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
                            Allowed types: {slot.allowedExtensions.join(", ")}
                        </div>
                    ) : null}
                    <TaskFileUploader
                        grant={grant}
                        step={step}
                        xsrfToken={xsrfToken ?? ""}
                        isDisabled={false}
                        fileType={CommentType.None}
                        fileLabel={slot.label}
                        allowedExtensions={uploadExtensions(slot.allowedExtensions) ?? undefined}
                        onUploadComplete={closeSlotDialog}
                        // Replace: delete the existing file just before the new one uploads.
                        beforeUpload={existing ? () => deleteFileForReplace!(existing.id) : undefined}
                    />
                </div>
            );
        };

        return (
            <div ref={listRef} className="submission-slot-list" style={{ padding: "5px" }} data-testid="submission-slot-list">
                <DataGrid<SlotRow, string>
                    dataSource={slots}
                    keyExpr="label"
                    showColumnHeaders={true}
                    showColumnLines={false}
                    hoverStateEnabled={true}
                    noDataText="No slots configured"
                >
                    <LoadPanel enabled={false} />
                    {/* Three columns only, no column hiding: the grid sits beside the instructions
                        and there is no room for more. The rest of the file's details are in the
                        link's tooltip. */}
                    {/* A share rather than a split: the file column gets the rest, and with it the
                        room to show the owner beside the date when the list is wide. */}
                    <Column caption="Document" dataField="label" width="28%" cellRender={(c: any) => <span style={{ fontWeight: 600 }}>{c.data?.label}</span>} />
                    <Column caption="File" headerCellRender={() => <FileListHeader nameLabel="File" />} cellRender={fileLink} />
                    {/* Wide enough for Replace + delete inside the cell's 7px padding. Grid cells
                        clip their overflow, and the buttons are right-aligned, so a column any
                        narrower cuts the left border off the Replace button. */}
                    <Column caption="" width={compact ? 90 : 180} cellRender={actionsCell} />
                    <Paging enabled={false} />
                </DataGrid>

                {!!orphanFiles?.length && (
                    <div
                        data-testid="orphan-files"
                        style={{
                            marginTop: 8,
                            fontSize: 12,
                            color: "#b54708",
                            background: "#fffaeb",
                            border: "1px solid #fedf89",
                            borderRadius: 4,
                            padding: "8px 10px",
                        }}
                    >
                        <span id={orphanTooltipId} data-testid="orphan-files-toggle" style={{ fontWeight: 600 }}>
                            {orphanFiles.length === 1 ? "1 orphan file" : `${orphanFiles.length} orphan files`} in
                            SharePoint - left by a document no longer in this task's setup
                        </span>
                        {orphanFolderUrl && (
                            <>
                                {" - "}
                                <a href={orphanFolderUrl} target="_blank" rel="noreferrer">open the folder</a>
                            </>
                        )}
                        {/* Hover for the names. The folder link sits on the line rather than inside
                            here: a tooltip that leaves on mouseleave cannot be clicked into. */}
                        <Tooltip
                            target={`#${orphanTooltipId}`}
                            showEvent="mouseenter"
                            hideEvent="mouseleave"
                            hideOnOutsideClick={false}
                            position="top"
                        >
                            <div data-testid="orphan-files-tooltip" style={{ padding: "8px 10px", maxWidth: "320px", textAlign: "left", fontSize: "12px" }}>
                                <div style={{ fontWeight: 600, marginBottom: 4 }}>Not attached to any document</div>
                                {orphanFiles.map((f) => (
                                    <div key={f.id} style={{ wordBreak: "break-word" }}>{f.name}</div>
                                ))}
                            </div>
                        </Tooltip>
                    </div>
                )}

                <Popup
                    visible={slotDialog?.open ?? false}
                    onHiding={closeSlotDialog}
                    title={slotDialog?.slot ? `Upload: ${slotDialog.slot.label}` : "Upload"}
                    width={480}
                    height={320}
                    showCloseButton={true}
                    hideOnOutsideClick={true}
                    deferRendering={false}
                    contentRender={renderDialogContent}
                />
            </div>
        );
    }

    const renderName = (data: any) => {
        const f = data?.data as FileDto;
        return <FileLink file={f} onClick={() => onNameClick(f.id, f.name)} showInfo={showFileInfo} />;
    };

    return (
        <div ref={listRef}>
            <DataGrid<FileDto, string>
                dataSource={dataSource}
                showColumnHeaders={showFileInfo && !compact}
                repaintChangesOnly={true}
                showColumnLines={false}
                hoverStateEnabled={true}
                visible={visible}
                height={"auto"}
                style={{ maxHeight: 245 }}
                noDataText={noDataText}
            >
                <LoadPanel enabled={false} />
                {/* One column: the link carries the date and owner, and the tooltip the rest. No
                    adaptive column hiding, so there is no "..." detail row to open. The headings are
                    laid out by the same component as the values so they line up. */}
                <Column dataField="name" caption="Name" headerCellRender={() => <FileListHeader />} cellRender={renderName} />
                <Column type="buttons" width={25}>
                    <Button name="delete" icon="trash" />
                </Column>
                <Column width={10}></Column>
                <Editing
                    allowDeleting={(fileData: any) => allowDeleting(fileData)}
                    texts={{ confirmDeleteMessage: "Are you sure you want to delete this file?" }}
                />
                <Scrolling mode="virtual" showScrollbar="always"></Scrolling>
                <Toolbar visible={true}>
                    <Item location="left" visible={showHeader}>
                        <h6 style={{ paddingLeft: "10px" }}>{headerText}</h6>
                    </Item>
                    {canDownloadAll && (
                        <Item
                            location="after"
                            locateInMenu="auto"
                            showText="inMenu"
                            widget="dxButton"
                            options={downloadButtonOptions}
                        />
                    )}
                </Toolbar>
            </DataGrid>
        </div>
    );
}
